import React from 'react';
import { Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChartsLoadingProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  dotColor?: string;
  animationSpeed?: 'slow' | 'normal' | 'fast';
}

const ChartsLoading: React.FC<ChartsLoadingProps> = ({
  className,
  size = 'md',
  color = 'transparent',
  dotColor = '#5959EB',
  animationSpeed = 'normal'
}) => {
  const sizeClasses = {
    sm: {
      container: 'w-16 h-3',
      dot: 'w-2 h-2',
      gap: 'gap-2'
    },
    md: {
      container: 'w-16 h-6',
      dot: 'w-2.5 h-2.5',
      gap: 'gap-2'
    },
    lg: {
      container: 'w-16 h-8',
      dot: 'w-3 h-3',
      gap: 'gap-3'
    }
  };

  const speedClasses = {
    slow: 'animate-pulse',
    normal: 'animate-bounce-large',
    fast: 'animate-ping'
  };

  const currentSize = sizeClasses[size];
  const speedClass = speedClasses[animationSpeed];

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        currentSize.container,
        currentSize.gap,
        className
      )}
      style={{ backgroundColor: color }}
    >
      {/* 第一个点 */}
      <Circle
        className={cn('rounded-full', currentSize.dot, speedClass)}
        color={dotColor}
        size={8}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0ms',
          animationDuration:
            animationSpeed === 'slow' ? '2s' : animationSpeed === 'fast' ? '0.5s' : '1s'
        }}
      />

      {/* 第二个点 */}
      <Circle
        className={cn('rounded-full', currentSize.dot, speedClass)}
        color={dotColor}
        size={8}
        style={{
          backgroundColor: dotColor,
          animationDelay: '200ms',
          animationDuration:
            animationSpeed === 'slow' ? '2s' : animationSpeed === 'fast' ? '0.5s' : '1s'
        }}
      />

      {/* 第三个点 */}
      <Circle
        className={cn('rounded-full', currentSize.dot, speedClass)}
        color={dotColor}
        size={8}
        style={{
          backgroundColor: dotColor,
          animationDelay: '400ms',
          animationDuration:
            animationSpeed === 'slow' ? '2s' : animationSpeed === 'fast' ? '0.5s' : '1s'
        }}
      />
    </div>
  );
};

// 自定义动画的加载组件
const ChartsLoadingCustom: React.FC<ChartsLoadingProps> = ({
  className,
  size = 'md',
  color = '#1F1F1F',
  dotColor = '#5959EB'
}) => {
  const sizeClasses = {
    sm: {
      container: 'w-16 h-3',
      dot: 'w-2 h-2',
      gap: 'gap-1'
    },
    md: {
      container: 'w-16 h-6',
      dot: 'w-2.5 h-2.5',
      gap: 'gap-2'
    },
    lg: {
      container: 'w-16 h-8',
      dot: 'w-3 h-3',
      gap: 'gap-3'
    }
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        currentSize.container,
        currentSize.gap,
        className
      )}
      style={{ backgroundColor: color }}
    >
      {/* 第一个点 */}
      <div
        className={cn('animate-bounce rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0ms',
          animationDuration: '1.4s'
        }}
      />

      {/* 第二个点 */}
      <div
        className={cn('animate-bounce rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0.2s',
          animationDuration: '1.4s'
        }}
      />

      {/* 第三个点 */}
      <div
        className={cn('animate-bounce rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0.4s',
          animationDuration: '1.4s'
        }}
      />
    </div>
  );
};

// 波浪动画加载组件
const ChartsLoadingWave: React.FC<ChartsLoadingProps> = ({
  className,
  size = 'md',
  color = '#1F1F1F',
  dotColor = '#5959EB'
}) => {
  const sizeClasses = {
    sm: {
      container: 'w-16 h-3',
      dot: 'w-2 h-2',
      gap: 'gap-1'
    },
    md: {
      container: 'w-16 h-6',
      dot: 'w-2.5 h-2.5',
      gap: 'gap-2'
    },
    lg: {
      container: 'w-16 h-8',
      dot: 'w-3 h-3',
      gap: 'gap-3'
    }
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        currentSize.container,
        currentSize.gap,
        className
      )}
      style={{ backgroundColor: color }}
    >
      {/* 第一个点 */}
      <div
        className={cn('rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animation: 'wave 1.4s ease-in-out infinite',
          animationDelay: '0ms'
        }}
      />

      {/* 第二个点 */}
      <div
        className={cn('rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animation: 'wave 1.4s ease-in-out infinite',
          animationDelay: '0.2s'
        }}
      />

      {/* 第三个点 */}
      <div
        className={cn('rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animation: 'wave 1.4s ease-in-out infinite',
          animationDelay: '0.4s'
        }}
      />

      <style>{`
        @keyframes wave {
          0%,
          60%,
          100% {
            transform: translateY(0);
          }
          30% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
};

// 脉冲动画加载组件
const ChartsLoadingPulse: React.FC<ChartsLoadingProps> = ({
  className,
  size = 'md',
  color = 'transparent',
  dotColor = '#5959EB'
}) => {
  const sizeClasses = {
    sm: {
      container: 'w-16 h-3',
      dot: 'w-2 h-2',
      gap: 'gap-1'
    },
    md: {
      container: 'w-16 h-6',
      dot: 'w-2.5 h-2.5',
      gap: 'gap-2'
    },
    lg: {
      container: 'w-16 h-8',
      dot: 'w-3 h-3',
      gap: 'gap-3'
    }
  };

  const currentSize = sizeClasses[size];

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        currentSize.container,
        currentSize.gap,
        className
      )}
      style={{ backgroundColor: color }}
    >
      {/* 第一个点 */}
      <div
        className={cn('animate-pulse rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0ms',
          animationDuration: '1.5s'
        }}
      />

      {/* 第二个点 */}
      <div
        className={cn('animate-pulse rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0.3s',
          animationDuration: '1.5s'
        }}
      />

      {/* 第三个点 */}
      <div
        className={cn('animate-pulse rounded-full', currentSize.dot)}
        color={dotColor}
        style={{
          backgroundColor: dotColor,
          animationDelay: '0.6s',
          animationDuration: '1.5s'
        }}
      />
    </div>
  );
};

export default ChartsLoading;
export { ChartsLoadingCustom, ChartsLoadingWave, ChartsLoadingPulse };
